import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';

import { Drawer, Toolbar, List, ClickAwayListener, ListItem } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import clsx from 'clsx';

// Icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PeopleIcon from '@mui/icons-material/People';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsIcon from '@mui/icons-material/Settings';

const NAVIGATION_ITEMS = [
    {
        id: 'pm-view',
        label: 'PM View',
        tooltip: 'PM View',
        icon: PeopleIcon,
        path: '/pm-view'
    },
    {
        id: 'project-view',
        label: 'Project View',
        tooltip: 'Project View',
        icon: HomeWorkIcon,
        path: '/project-view'
    },
    {
        id: 'super-view',
        label: 'Super View',
        tooltip: 'Super View',
        icon: EngineeringIcon,
        path: '/super-view'
    },
    {
        id: 'custom-settings',
        label: 'Custom Settings',
        tooltip: 'Custom Settings',
        icon: SettingsIcon,
        path: '/custom-settings'
    }
];

// Constants
const DRAWER_WIDTH = 210;
const COLLAPSED_DRAWER_WIDTH = 57; // theme.spacing(7) + 1

const PREFIX = 'Sidebar';

const classes = {
    root: `${PREFIX}-root`,
    menuButton: `${PREFIX}-menuButton`,
    hide: `${PREFIX}-hide`,
    drawer: `${PREFIX}-drawer`,
    drawerOpen: `${PREFIX}-drawerOpen`,
    drawerClose: `${PREFIX}-drawerClose`,
    active: `${PREFIX}-active`,
    toolbar: `${PREFIX}-toolbar`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        display: 'flex',
        zIndex: 9,
        color: '#d8d8d8'
    },

    [`& .${classes.menuButton}`]: {
        marginRight: 36,
    },

    [`& .${classes.hide}`]: {
        display: 'none',
    },

    [`& .${classes.drawer}`]: {
        width: DRAWER_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },

    [`& .${classes.drawerOpen}`]: {
        width: DRAWER_WIDTH,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },

    [`& .${classes.drawerClose}`]: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: COLLAPSED_DRAWER_WIDTH,
        [theme.breakpoints.up('sm')]: {
            width: COLLAPSED_DRAWER_WIDTH,
        },
    },

    [`& .${classes.active}`]: {
        backgroundColor: '#d8d8d8',
    },

    [`& .${classes.toolbar}`]: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(6, 1, 0, 0),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
    }
}));

/**
 * Custom NavLink component that forwards refs and applies active class styling
 */
const NavLinkMui = React.forwardRef((props, ref) => {
    return <NavLink {...props} ref={ref} activeclassname={classes.active} />;
});

NavLinkMui.propTypes = {
    to: PropTypes.string.isRequired,
    children: PropTypes.node
};

NavLinkMui.displayName = 'NavLinkMui';

/**
 * Reusable navigation item component with consistent styling and accessibility features
 * @param {Object} item - Navigation item configuration
 * @param {string} className - Additional CSS class for styling
 */
const NavigationItem = React.memo(({ item, className }) => {
    const IconComponent = item.icon;
    
    return (
        <Tooltip title={item.tooltip}>
            <ListItem
                button
                key={item.id}
                className={className}
                component={NavLinkMui}
                to={item.path}
                role="menuitem"
                aria-label={`Navigate to ${item.label}`}
            >
                <ListItemIcon>
                    <IconComponent aria-hidden="true" />
                </ListItemIcon>
                <ListItemText 
                    primary={item.label}
                    classes={{ primary: classes.sublist_text }} 
                />
            </ListItem>
        </Tooltip>
    );
});

NavigationItem.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        tooltip: PropTypes.string.isRequired,
        icon: PropTypes.elementType.isRequired,
        path: PropTypes.string.isRequired
    }).isRequired,
    className: PropTypes.string
};

NavigationItem.displayName = 'NavigationItem';

const Sidebar = React.memo(() => {
    const [open, setOpen] = React.useState(false);
    
    const handleDrawerOpen = React.useCallback(() => {
        setOpen(true);
    }, []);

    const handleDrawerClose = React.useCallback(() => {
        setOpen(false);
    }, []);

    const handleKeyDown = React.useCallback((event) => {
        if (event.key === 'Escape' && open) {
            handleDrawerClose();
        }
    }, [open, handleDrawerClose]);

    const drawerClasses = React.useMemo(() => clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
    }), [open]);

    const paperClasses = React.useMemo(() => clsx({
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
    }), [open]);

    return (
        <Root className={classes.root}>
            <CssBaseline />
            <Drawer
                variant="permanent"
                className={drawerClasses}
                classes={{
                    paper: paperClasses,
                }}
                aria-label={open ? "Expanded navigation menu" : "Collapsed navigation menu"}
                onKeyDown={handleKeyDown}
            >
                <ClickAwayListener onClickAway={handleDrawerClose}>
                    <Toolbar className={classes.toolbar} role="navigation" aria-label="Main navigation">
                        <List role="menu">
                            <ListItem
                                button
                                key={'collapse-expand'}
                                aria-label={open ? "Collapse navigation menu" : "Expand navigation menu"}
                                aria-expanded={!open}
                                onClick={handleDrawerOpen}
                                role="menuitem"
                                tabIndex={open ? -1 : 0}
                                className={clsx({
                                    [classes.hide]: open,
                                })}
                            >
                                <ListItemIcon>
                                    <ChevronRightIcon />
                                </ListItemIcon>
                                <ListItemText primary={'Views'} />
                            </ListItem>
                            <ListItem
                                button
                                key={'collapse-expand-close'}
                                aria-label="Collapse navigation menu"
                                aria-expanded={open}
                                onClick={handleDrawerClose}
                                role="menuitem"
                                tabIndex={!open ? -1 : 0}
                                className={clsx({
                                    [classes.hide]: !open,
                                })}
                            >
                                <ListItemIcon>
                                    <ChevronLeftIcon />
                                </ListItemIcon>
                                <ListItemText primary={'Views'} />
                            </ListItem>
                            
                            {/* Navigation Items */}
                            {NAVIGATION_ITEMS.map((item) => (
                                <NavigationItem 
                                    key={item.id}
                                    item={item}
                                    className={classes.sublist}
                                />
                            ))}

                        </List>
                    </Toolbar>
                </ClickAwayListener>
            </Drawer>
        </Root>
    );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
